import React, { useState, useEffect } from "react";
import { Wrapper } from "./style";
import { MakeApiCall } from "../../../apis";
import {
  message,
  Table,
  Button,
  Checkbox,
  Badge,
  ConfigProvider,
  Pagination,
  Menu,
  Upload,
  Dropdown,
  Input,
} from "antd";
import { Link } from "react-router-dom";
import { fakeProImage } from "../../../config/fakeData";
import { CheckOutlined, CloseOutlined, DownloadOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet";
import AddCogs from "./lib/addCogs";

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

const Products = (props) => {
  const { setData, userData } = props;

  const [totalPage, setTotalPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [editingCell, setEditingCell] = useState({ key: null, field: null });
  const [tempValue, setTempValue] = useState("");

  const getProductList = async (data) => {
    setLoading(true);
    const response = await MakeApiCall(
        `get-products?search_text=${data?.searchText || searchText}&page=${
            data?.page || page || 1
        }&per-page=${data?.pageSize || pageSize}`,
        "get",
        null,
        true,
        {},
        userData?.token
    );

    if (response?.status) {
      setTotalPage(response?.data?.pagination?.totalCount || 0);
      setPage(response?.data?.pagination?.page || 1);
      setList(response?.data?.records || []);
      setLoading(false);
    } else {
      message.warning(response?.message);
      setLoading(false);
      setList([]);
    }
  };

  const updateField = async (record, field, newValue) => {
    const payload = {
      [field]: newValue
    };

    const response = await MakeApiCall(
        `update-product/${record.id}`,
        "post",
        payload,
        true,
        null,
        userData?.token
    );

    if (response?.status) {
      message.success("Updated successfully!");
      getProductList();
    } else {
      message.error(response?.message || "Update failed");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await getProductList({ page, pageSize });
    };
    fetchData();
  }, [page, pageSize, searchText]);

  const startEditing = (record, field, value) => {
    setEditingCell({ key: record.id, field });
    setTempValue(value ?? "");
  };

  const finishEditing = async (record, field) => {
    setEditingCell({ key: null, field: null });
    await updateField(record, field, tempValue);
  };

  const editableCell = (record, field, value) => {
    const isEditing =
        editingCell.key === record.id && editingCell.field === field;

    if (!isEditing) {
      return (
          <span
              onClick={() => startEditing(record, field, value)}
              style={{ cursor: "pointer" }}
          >
        {value ?? "N/A"}
      </span>
      );
    }

    return (
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <Input
              autoFocus
              size="small"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              style={{ width: "120px" }}
              onPressEnter={() => finishEditing(record, field)}
          />

          {/* Tick Icon (Save) */}
          <CheckOutlined
              onClick={() => finishEditing(record, field)}
              style={{
                color: "green",
                fontSize: 16,
                cursor: "pointer",
              }}
          />

          {/* Cross Icon (Cancel) */}
          <CloseOutlined
              onClick={() => {
                setTempValue(value ?? "");
                setEditingCell({ key: null, field: null });
              }}
              style={{
                color: "red",
                fontSize: 16,
                cursor: "pointer",
              }}
          />
        </div>
    );
  };

  const columns = [
    {
      title: "#",
      key: "index",
      render: (text, record, index) => {
        return (page - 1) * pageSize + index + 1;
      },
    },
    {
      title: "User Details",
      dataIndex: "userDetails",
      key: "userDetails",
      render: (_, record) => (
          <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
            <div className="symbol symbol-65px overflow-hidden me-3">
              <img src={record?.item_image || fakeProImage} alt />
            </div>
            <div className="d-flex flex-column">
              <a
                className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-200px"
                href={record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#"}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record?.item_name || ""}
              </a>
              <div className="fs-7 mt-1">
              <span>
                <b>SKU:</b> {record?.sku}
              </span>
                {" / "}
                <span>
                <b>ASIN:</b> {record?.asin}
              </span>
              </div>
              <div className="fs-7 mt-1">
              <span>
                <b>
                  {record?.currency} {record?.item_price}
                </b>
              </span>
                {" / "}
                <span> {record?.item_type}</span>
              </div>
            </div>
          </div>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      render: (value, record) => editableCell(record, "tags", value),
    },
    {
      title: "COGS",
      dataIndex: "item_cogs",
      key: "item_cogs",
      render: (cogs, record) => (
          <div className="d-flex align-items-center">
            {cogs || "N/A"}
            <Link
                to=""
                onClick={() => handleEditCogs(record)}
                style={{ marginLeft: "10px", color: "#333" }}
            >
              <i className="ki-outline ki-plus-circle" />
            </Link>
          </div>
      ),
    },
    {
      title: "FBM shipping cost",
      dataIndex: "fbm_shipping_cost_per_package",
      key: "fbm_shipping_cost_per_package",
      render: (value, record) => editableCell(record, "fbm_shipping_cost_per_package", value),
    },
    {
      title: "Val. of unsellable return (%)",
      dataIndex: "unsellable_returns_percentage",
      key: "unsellable_returns_percentage",
      render: (value, record) => editableCell(record, "unsellable_returns_percentage", value),
    },
    {
      title: "Vat Product Tax Code",
      dataIndex: "product_tax_code",
      key: "product_tax_code",
      render: (value, record) => editableCell(record, "product_tax_code", value),
    },
    {
      title: "Vat Rate",
      dataIndex: "vat_rate",
      key: "vat_rate",
      render: (value, record) => editableCell(record, "vat_rate", value),
    },
    {
      title: "Hide",
      dataIndex: "hide",
      key: "hide",
      render: (hide) => <Checkbox defaultChecked={hide} />,
      align: "center",
    },
  ];

  const theme = {
    token: {
      itemActiveBg: "#ffffff",
      itemActiveBgDisabled: "rgba(0, 0, 0, 0.15)",
      itemActiveColorDisabled: "rgba(0, 0, 0, 0.25)",
      itemBg: "#ffffff",
      itemInputBg: "#ffffff",
      itemLinkBg: "#ffffff",
      itemSize: 32,
      itemSizeSM: 24,
      miniOptionsSizeChangerTop: 0,
      colorBgContainer: "#ffffff",
      colorBgContainerDisabled: "rgba(0, 0, 0, 0.04)",
      colorBgTextActive: "rgb(255, 255, 255)",
      colorBgTextHover: "rgb(255, 255, 255)",
      colorBorder: "#d9d9d9",
      colorPrimary: "#f45b68",
      colorPrimaryBorder: "#f45b68",
      colorPrimaryHover: "#f45b68",
      colorText: "rgba(0, 0, 0, 0.88)",
      colorTextDisabled: "rgba(0, 0, 0, 0.25)",
      colorTextPlaceholder: "rgba(0, 0, 0, 0.25)",
    },
  };

  const handleUpload = (file) => {
    message.success(`${file.name} file uploaded successfully.`);
    return false;
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setPage(1);
  };

  const menu = (
      <Menu>
        <Menu.Item key="1" className="py-4" disabled>
          <div className="fs-5 text-gray-900 fw-bold">Import product data</div>
        </Menu.Item>
        <Menu.Divider />
        <div key="4" className="py-2 px-3">
          <div className="d-flex justify-content-between w-100">
            <p className="mb-0">Download a template</p>
            <a href="/assets/media/templates/Product_Import_Template.xlsx"
               download="product_template.xlsx"
               className="text-danger">Template <DownloadOutlined />
            </a>
          </div>
        </div>
        <Menu.Divider />
        <div key="4" className="py-2 px-3">
          <Upload beforeUpload={handleUpload}>
            <Button className="w-100 fs-7 h-45px border border-dashed border-danger">
              <i className="ki-duotone ki-file-up fs-2x text-danger">
                <span className="path1" />
                <span className="path2" />
              </i>
              Drop files here or click to upload
            </Button>
          </Upload>
        </div>
        <Menu.Divider />
        <div key="4" className="py-2 px-3">
          <div className="d-flex justify-content-end">
            <button type="reset" className="btn btn-sm btn-light btn-active-light-danger me-2">
              Reset
            </button>
            <button type="submit" className="btn btn-sm btn-danger">Apply</button>
          </div>
        </div>
      </Menu>
  );

  const handleEditCogs = (record) => {
    setSelectedProduct(record);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedProduct(null);
  };

  return (
      <Wrapper>
        <Helmet>
          <title>Product</title>
        </Helmet>

        <div className="mb-10 mx-n9 px-10 py-5 bg-white filter">
          <div className="position-relative ">
            <i className="ki-outline ki-magnifier text-gray-800 fs-2 position-absolute top-50 translate-middle-y ms-4" />
            <input
                type="text"
                value={searchText}
                onChange={handleSearch}
                className="form-control form-control-solid text-dark w-300px border border-gray-300 fs-7 ps-12 all-search"
                placeholder="Search by sku, title or asin..."
            />
          </div>
        </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed">
                <h3 className="card-title fw-bolder">Products List</h3>
                <div className="card-toolbar">
                  <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight">
                    <button className="btn btn-danger fs-7 fw-bold">Import <i className="ms-1 bi bi-download fs-4" /></button>
                  </Dropdown>
                </div>
              </div>

              <div className="card-body py-2">
                <div className="table-responsive">
                  <Table
                      scroll={{ x: "max-content" }}
                      columns={columns}
                      loading={loading}
                      dataSource={list}
                      rowKey="id"
                      pagination={false}
                      components={{
                        table: (props) => (
                            <table className="table table-row-dashed table-row-gray-300 align-middle gs-4 gy-4 fs-7" {...props} />
                        ),
                        header: {
                          wrapper: (props) => <thead {...props} />,
                          row: (props) => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                          cell: (props) => <th {...props} />,
                        },
                      }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-body py-2">
                <ConfigProvider theme={theme}>
                  <Pagination
                      className="mt-3"
                      current={page}
                      pageSize={pageSize}
                      total={totalPage}
                      showSizeChanger
                      onChange={(currentPage, newPageSize) => {
                        setPage(currentPage);
                        setPageSize(newPageSize);
                      }}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                  />
                </ConfigProvider>
              </div>
            </div>
          </div>
        </div>

        <AddCogs
            visible={isModalVisible}
            onClose={handleCloseModal}
            product={selectedProduct}
            userData={userData}
            getList={() => getProductList()}
        />
      </Wrapper>
  );
};

export default Products;

